/**
 * Part 11/12 — OMS audit / supporting information in Xero.
 *
 * Preferred combination (Part 12):
 * A. OMS Purchase Audit PDF attachment — primary finance narrative
 * B. Reference — supplier invoice # + OMS PO (lean identifiers)
 * C. Url — “Go to OMS” deep link for authorised users
 * History note — short pointer to the attachment + OMS link (not a dump)
 */

import {
  buildInvoicesDeepLinkPath,
  buildReceiptingDeepLinkPath,
} from './reconciliationHealth'
import {
  buildOmsPurchaseAuditDocument,
  buildOmsXeroAuditHistoryPointer,
} from './omsPurchaseAudit'
import {
  sanitizeOmsDeepLinkUrl,
  sanitizePersonDisplayName,
  sanitizeTextForXeroAccounting,
} from './xeroExportSanitise'

/** Soft cap — Xero History Details is short; keep finance-readable. */
export const OMS_XERO_HISTORY_MAX_CHARS = 2000

function isCreditNoteDocument(document = {}) {
  const type = String(document?.documentType || 'invoice')
    .trim()
    .toLowerCase()
  return type === 'creditnote' || type === 'credit_note'
}

/**
 * Dashboard origin for absolute deep links (Xero Url must be absolute).
 */
export function resolveOmsDashboardBaseUrl(explicit) {
  const fromArg = String(explicit || '').trim().replace(/\/$/, '')
  if (fromArg) return fromArg
  if (typeof process !== 'undefined') {
    const env =
      process.env?.REACT_APP_DASHBOARD_URL ||
      process.env?.FRONTEND_BASE_URL ||
      ''
    const cleaned = String(env).trim().replace(/\/$/, '')
    if (cleaned) return cleaned
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin).replace(/\/$/, '')
  }
  return ''
}

/**
 * Deep link a finance user can open from Xero (“Go to OMS”).
 * Prefer human invoice/PO search over Mongo document ids when possible.
 * Never embeds tokens — OMS login + venue auth still required on open.
 */
export function buildOmsXeroSourceUrl({
  venueId,
  order = {},
  document = {},
  omsBaseUrl,
} = {}) {
  const venue = String(venueId || '').trim()
  if (!venue) return null

  const base = resolveOmsDashboardBaseUrl(omsBaseUrl)
  if (!base) return null

  const po = String(order?.purchaseOrderNumber || '').trim()
  const invoiceNumber = String(
    document?.invoiceNumber || order?.invoiceNumber || '',
  ).trim()
  const docId = String(document?._id || document?.id || '').trim()

  let path
  // Prefer invoice number (+ optional PO) so Url does not rely on internal ids.
  if (invoiceNumber) {
    path = buildInvoicesDeepLinkPath(venue, {
      q: invoiceNumber,
      po: po || undefined,
    })
  } else if (po) {
    path = buildReceiptingDeepLinkPath(venue, { q: po })
  } else if (docId) {
    path = buildInvoicesDeepLinkPath(venue, { invoiceId: docId })
  } else {
    path = buildReceiptingDeepLinkPath(venue)
  }

  return sanitizeOmsDeepLinkUrl(
    `${base}${path.startsWith('/') ? path : `/${path}`}`,
  )
}

function formatShortDate(value) {
  if (!value) return null
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString().slice(0, 10)
}

function summariseReceiptingAudit(receiptingProducts = []) {
  const rows = (
    Array.isArray(receiptingProducts) ? receiptingProducts.flat() : []
  ).filter(row => row?.supplierConfirmed === true)

  const receiptedBy = new Set()
  let earliestReceipt = null
  let discrepancyLines = 0

  for (const row of rows) {
    const r = row?.receipting || {}
    const by = sanitizePersonDisplayName(
      r.originalReceiptedBy || r.editedBy || '',
    )
    if (by) receiptedBy.add(by)
    const at = r.originalReceiptedAt || r.approvedDate || r.createdAt
    if (at) {
      const t = new Date(at).getTime()
      if (!Number.isNaN(t) && (earliestReceipt == null || t < earliestReceipt)) {
        earliestReceipt = t
      }
    }
    const orderedQty = Number(r.orderedQuantity ?? row?.orderedQuantity)
    const updatedQty = Number(r.updatedQuantity ?? row?.quantity)
    const orderedPrice = Number(r.orderedPrice ?? row?.product?.price)
    const updatedPrice = Number(r.updatedPrice)
    const qtyChanged =
      Number.isFinite(orderedQty) &&
      Number.isFinite(updatedQty) &&
      orderedQty !== updatedQty
    const priceChanged =
      Number.isFinite(orderedPrice) &&
      Number.isFinite(updatedPrice) &&
      Math.abs(orderedPrice - updatedPrice) > 0.0001
    if (qtyChanged || priceChanged || r.isAlternativeReceived) {
      discrepancyLines += 1
    }
  }

  return {
    confirmedLineCount: rows.length,
    receiptedBy: [...receiptedBy],
    receiptDate: earliestReceipt
      ? formatShortDate(new Date(earliestReceipt))
      : null,
    discrepancyLines,
  }
}

function summariseInvoiceReconciliation(document = {}) {
  const allocations = Array.isArray(document?.poAllocations)
    ? document.poAllocations
    : []
  const names = [
    ...new Set(
      allocations
        .map(a => sanitizePersonDisplayName(a?.reconciledByName))
        .filter(Boolean),
    ),
  ]
  const dates = allocations
    .map(a => formatShortDate(a?.reconciledAt))
    .filter(Boolean)
  return {
    reconciledBy: names,
    reconciledDate: dates[0] || null,
  }
}

/**
 * One compact History note for Xero — finance traceability, not clutter.
 */
export function buildOmsXeroHistoryNote({
  venue = {},
  order = {},
  venueSupplier = {},
  document = {},
  receiptingProducts = [],
  omsUrl = null,
  documentType,
} = {}) {
  const isCredit =
    documentType === 'creditNote' ||
    documentType === 'credit' ||
    isCreditNoteDocument(document)
  const kind = isCredit ? 'Credit note' : 'Bill'
  const lines = [`OMS ${kind} — source summary`]

  const supplier =
    venueSupplier?.supplierTitle ||
    venueSupplier?.title ||
    order?.supplier?.title ||
    order?.supplierTitle ||
    null
  if (supplier) lines.push(`Supplier: ${supplier}`)

  const po = String(order?.purchaseOrderNumber || '').trim()
  if (po) lines.push(`PO: ${po}`)

  const venueTitle = String(venue?.title || '').trim()
  if (venueTitle) lines.push(`Venue: ${venueTitle}`)

  const docNumber = String(document?.invoiceNumber || '').trim()
  if (docNumber) {
    lines.push(`${isCredit ? 'Credit note' : 'Supplier invoice'}: ${docNumber}`)
  }

  const placedBy = sanitizePersonDisplayName(order?.placedBy)
  if (placedBy) lines.push(`Ordered by: ${placedBy}`)

  const approvalStatus = String(order?.approvalStatus || '').trim()
  const approvedBy = sanitizePersonDisplayName(order?.approvalDecisionBy)
  if (approvalStatus && approvalStatus !== 'none') {
    const when = formatShortDate(order?.approvalDecisionAt)
    lines.push(
      [
        `Approval: ${approvalStatus}`,
        approvedBy && `by ${approvedBy}`,
        when,
      ]
        .filter(Boolean)
        .join(' '),
    )
  }

  const receipt = summariseReceiptingAudit(receiptingProducts)
  if (receipt.confirmedLineCount > 0) {
    const who =
      receipt.receiptedBy.length > 0
        ? receipt.receiptedBy.join(', ')
        : null
    lines.push(
      [
        `Goods receipt: ${receipt.confirmedLineCount} confirmed line(s)`,
        who && `by ${who}`,
        receipt.receiptDate,
      ]
        .filter(Boolean)
        .join(' · '),
    )
    if (receipt.discrepancyLines > 0) {
      lines.push(
        `Discrepancies: ${receipt.discrepancyLines} line(s) with qty/price/alternative changes`,
      )
    }
  }

  const recon = summariseInvoiceReconciliation(document)
  if (recon.reconciledBy.length || recon.reconciledDate) {
    lines.push(
      [
        'Reconciled',
        recon.reconciledBy.length
          ? `by ${recon.reconciledBy.join(', ')}`
          : null,
        recon.reconciledDate,
      ]
        .filter(Boolean)
        .join(' '),
    )
  }

  // Part 13 — do not dump internal OMS auditHistory / debug events into Xero.

  const url = sanitizeOmsDeepLinkUrl(omsUrl)
  if (url) lines.push(`View in OMS: ${url}`)
  else lines.push('View in OMS: Goods Receipting / Invoices (open OMS)')

  let note = sanitizeTextForXeroAccounting(lines.join('\n'))
  if (note.length > OMS_XERO_HISTORY_MAX_CHARS) {
    note = `${note.slice(0, OMS_XERO_HISTORY_MAX_CHARS - 1)}…`
  }
  return note
}

/**
 * Attach Xero Url + OMS-only audit metadata (stripped before Xero create).
 * Detail lives on the attached OMS Purchase Audit PDF (Part 12).
 */
export function withOmsXeroAuditTrail(
  billData = {},
  {
    venue = {},
    order = {},
    venueSupplier = {},
    document = {},
    receiptingProducts = [],
    financeDocuments = [],
    omsBaseUrl,
  } = {},
) {
  if (!billData || typeof billData !== 'object') return billData

  const omsUrl = buildOmsXeroSourceUrl({
    venueId: venue?._id || venue?.id,
    order,
    document,
    omsBaseUrl,
  })

  const audit = buildOmsPurchaseAuditDocument({
    venue,
    order,
    venueSupplier,
    document,
    financeDocuments,
    receiptingProducts,
    omsUrl,
  })

  const isCredit =
    billData?.Type === 'ACCPAYCREDIT' || isCreditNoteDocument(document)

  const omsHistoryNote = buildOmsXeroAuditHistoryPointer({
    isCreditNote: isCredit,
    purchaseOrderNumber: order?.purchaseOrderNumber,
    omsUrl,
    auditFileName: audit.fileName,
  })

  const next = {
    ...billData,
    omsHistoryNote,
    omsAuditText: audit.text,
    omsAuditFileName: audit.fileName,
  }
  if (omsUrl) next.Url = omsUrl
  return next
}

/**
 * OMS-only keys that must never be sent on Invoices/CreditNotes create.
 */
export const OMS_XERO_BILL_METADATA_KEYS = [
  'omsHistoryNote',
  'omsAuditText',
  'omsAuditFileName',
  'allocateToXeroInvoiceId',
  'forceContactName',
  'vatRounding',
]

export function stripOmsXeroBillMetadata(billData = {}) {
  if (!billData || typeof billData !== 'object') {
    return { payload: billData, meta: {} }
  }
  const payload = { ...billData }
  const meta = {}
  for (const key of OMS_XERO_BILL_METADATA_KEYS) {
    if (key in payload) {
      meta[key] = payload[key]
      delete payload[key]
    }
  }
  return { payload, meta }
}
